import { useState } from 'react';
import PropTypes from 'prop-types';

import {
  Box,
  Card,
  Chip,
  Button,
  Accordion,
  Typography,
  CardHeader,
  CardContent,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';

import {
  isMasterUser,
  debugPermissions,
  ROUTE_PERMISSIONS,
  logPermissionDebug,
} from 'src/utils/permissions';

import { useAuthContext } from 'src/auth/hooks';

import Iconify from 'src/components/iconify';

// ----------------------------------------------------------------------

/**
 * ⚠️ COMPONENTE DE DEBUG PARA DESARROLLO
 * Muestra información detallada sobre permisos del usuario
 * Solo debe usarse en desarrollo - REMOVER en producción
 */
const PermissionDebugPanel = ({ route = null, showFullDebug = false }) => {
  const { user } = useAuthContext();
  const [expanded, setExpanded] = useState(false);

  // Solo mostrar en desarrollo
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  const debugInfo = debugPermissions(user, route);
  const isMaster = isMasterUser(user);

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  return (
    <Card
      sx={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        width: expanded ? 400 : 'auto',
        maxHeight: expanded ? '70vh' : 'auto',
        overflow: 'auto',
        zIndex: 9999,
        bgcolor: 'background.paper',
        border: '2px solid',
        borderColor: isMaster ? 'success.main' : 'warning.main',
      }}
    >
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Iconify icon="mdi:shield-account" />
            <Typography variant="subtitle2">{isMaster ? '🔓 MASTER' : '🔒 RESTRICTED'}</Typography>
          </Box>
        }
        action={
          <Button size="small" onClick={handleExpandClick}>
            {expanded ? 'Collapse' : 'Debug'}
          </Button>
        }
        sx={{ pb: 1 }}
      />

      {expanded && (
        <CardContent sx={{ pt: 0 }}>
          {/* Información del usuario */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<Iconify icon="eva:arrow-ios-downward-fill" />}>
              <Typography variant="subtitle2">👤 Usuario</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2">
                  <strong>Username:</strong> {debugInfo.user.username}
                </Typography>
                <Typography variant="body2">
                  <strong>Roles:</strong>{' '}
                  {debugInfo.user.roles.map((role) => (
                    <Chip
                      key={role}
                      label={role}
                      size="small"
                      color={role === 'master' ? 'success' : 'default'}
                      sx={{ ml: 0.5 }}
                    />
                  ))}
                </Typography>
                <Typography variant="body2">
                  <strong>Permisos:</strong> {debugInfo.user.permissionsCount}
                </Typography>
                <Typography variant="body2">
                  <strong>Es Master:</strong>{' '}
                  <Chip
                    label={debugInfo.user.isMaster ? 'SÍ' : 'NO'}
                    size="small"
                    color={debugInfo.user.isMaster ? 'success' : 'error'}
                  />
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Información de la ruta (si se proporciona) */}
          {route && debugInfo.route && (
            <Accordion>
              <AccordionSummary expandIcon={<Iconify icon="eva:arrow-ios-downward-fill" />}>
                <Typography variant="subtitle2">🛣️ Ruta Actual</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2">
                    <strong>Ruta:</strong> {debugInfo.route.route}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Puede Acceder:</strong>{' '}
                    <Chip
                      label={debugInfo.route.canAccess ? 'SÍ' : 'NO'}
                      size="small"
                      color={debugInfo.route.canAccess ? 'success' : 'error'}
                    />
                  </Typography>
                  <Typography variant="body2">
                    <strong>Permisos Requeridos:</strong>{' '}
                    {debugInfo.route.requiredPermissions.length === 0 ? (
                      <Chip label="Ninguno" size="small" color="warning" />
                    ) : (
                      debugInfo.route.requiredPermissions.map((perm) => (
                        <Chip key={perm} label={perm} size="small" sx={{ ml: 0.5, mb: 0.5 }} />
                      ))
                    )}
                  </Typography>
                </Box>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Lista de permisos del usuario */}
          {showFullDebug && (
            <Accordion>
              <AccordionSummary expandIcon={<Iconify icon="eva:arrow-ios-downward-fill" />}>
                <Typography variant="subtitle2">🔑 Todos los Permisos</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                  {debugInfo.permissions.userPermissions.map((permission) => (
                    <Chip
                      key={permission}
                      label={permission}
                      size="small"
                      sx={{ mr: 0.5, mb: 0.5 }}
                    />
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Mapeo de rutas */}
          {showFullDebug && (
            <Accordion>
              <AccordionSummary expandIcon={<Iconify icon="eva:arrow-ios-downward-fill" />}>
                <Typography variant="subtitle2">🗺️ Mapeo de Rutas</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                  <Typography variant="caption">
                    Total de rutas mapeadas: {Object.keys(ROUTE_PERMISSIONS).length}
                  </Typography>
                  <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {Object.entries(ROUTE_PERMISSIONS)
                      .slice(0, 10)
                      .map(([routePath, perms]) => (
                        <Box key={routePath} sx={{ fontSize: '0.75rem' }}>
                          <strong>{routePath}</strong> → {perms.join(', ')}
                        </Box>
                      ))}
                    {Object.keys(ROUTE_PERMISSIONS).length > 10 && (
                      <Typography variant="caption">
                        ... y {Object.keys(ROUTE_PERMISSIONS).length - 10} más
                      </Typography>
                    )}
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Botón para log completo */}
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => logPermissionDebug(user, route)}
              startIcon={<Iconify icon="mdi:console-line" />}
            >
              Log Debug Completo
            </Button>
          </Box>
        </CardContent>
      )}
    </Card>
  );
};

PermissionDebugPanel.propTypes = {
  route: PropTypes.string,
  showFullDebug: PropTypes.bool,
};

export default PermissionDebugPanel;

// ----------------------------------------------------------------------

/**
 * ⚠️ HOOK DE DEBUG PARA DESARROLLO
 * Permite activar/desactivar el panel de debug fácilmente
 */
export const usePermissionDebug = () => {
  const [debugEnabled, setDebugEnabled] = useState(false);

  const toggleDebug = () => setDebugEnabled(!debugEnabled);

  const DebugPanel = ({ route, showFullDebug = false }) =>
    debugEnabled ? <PermissionDebugPanel route={route} showFullDebug={showFullDebug} /> : null;

  DebugPanel.propTypes = {
    route: PropTypes.string,
    showFullDebug: PropTypes.bool,
  };

  return {
    debugEnabled,
    toggleDebug,
    DebugPanel,
  };
};
